import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Kısayol tuşları için hook
const useKeyboardShortcuts = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Güvenlik kontrolü: event.key var mı?
      if (!event.key) {
        return;
      }
      
      // F tuşları için varsayılan tarayıcı davranışını engelle
      if (event.key.startsWith('F') && event.key.length <= 3) {
        event.preventDefault();
      }

      // Alt veya Ctrl ile birlikte basılmışsa kısayolları çalıştırma
      if (event.altKey || event.ctrlKey) {
        return;
      }

      switch (event.key) {
        case 'F1':
          // Ana Panel (Dashboard)
          navigate('/admin/dashboard');
          showShortcutNotification('📊 Ana Panel açıldı');
          break;
          
        case 'F2':
          // Sipariş Sayfası
          navigate('/admin/orders');
          showShortcutNotification('📋 Sipariş Yönetimi açıldı');
          break;
          
        case 'F3':
          // Müşteri Yönetimi
          navigate('/admin/customers');
          showShortcutNotification('👥 Müşteri Yönetimi açıldı');
          break;
          
        case 'F4':
          // Ürün Yönetimi
          navigate('/admin/products');
          showShortcutNotification('📦 Ürün Yönetimi açıldı');
          break;
          
        case 'F5':
          // Raporlar (F5'i refresh olarak bırakıyoruz)
          // Bu durumda F6 kullanabiliriz
          break;
          
        case 'F6':
          // Raporlar
          navigate('/admin/reports');
          showShortcutNotification('📈 Raporlar açıldı');
          break;
          
        case 'F7':
          // Ana Sayfa'ya dön
          navigate('/');
          showShortcutNotification('🏠 Ana Sayfa açıldı');
          break;
          
        case 'F8':
          // Bugünkü siparişleri göster (Sipariş sayfasında bugün filtresi)
          navigate('/admin/orders?filter=today');
          showShortcutNotification('📅 Bugünkü siparişler açıldı');
          break;
          
        case 'F9':
          // Yeni sipariş modal'ını aç (eğer sipariş sayfasındaysak)
          if (window.location.pathname === '/admin/orders') {
            // Modal açma event'i gönder
            window.dispatchEvent(new CustomEvent('openNewOrderModal'));
            showShortcutNotification('➕ Yeni sipariş formu açıldı');
          } else {
            navigate('/admin/orders');
            // Biraz bekleyip modal açma event'i gönder
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('openNewOrderModal'));
            }, 500);
            showShortcutNotification('📋 Sipariş sayfası açıldı, yeni sipariş formu hazırlanıyor...');
          }
          break;
          
        case 'F10':
          // Kısayol yardımını göster
          showShortcutHelp();
          break;
          
        default:
          return; // Diğer tuşlar için hiçbir şey yapma
      }
    };

    // Event listener ekle
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [navigate]);

  // Kısayol bildirim fonksiyonu
  const showShortcutNotification = (message: string) => {
    // Mevcut bildirimleri temizle
    const existingNotifications = document.querySelectorAll('.shortcut-notification');
    existingNotifications.forEach(notification => notification.remove());

    // Yeni bildirim oluştur
    const notification = document.createElement('div');
    notification.className = 'shortcut-notification';
    notification.textContent = message;
    
    // Stil ekle
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: linear-gradient(135deg, #3b82f6, #1d4ed8);
      color: white;
      padding: 12px 20px;
      border-radius: 12px;
      font-weight: 600;
      font-size: 14px;
      box-shadow: 0 8px 25px rgba(59, 130, 246, 0.3);
      z-index: 9999;
      animation: slideInRight 0.3s ease-out;
      border: 1px solid rgba(255, 255, 255, 0.2);
    `;

    // CSS animasyon ekle (eğer yoksa)
    if (!document.querySelector('#shortcut-animations')) {
      const style = document.createElement('style');
      style.id = 'shortcut-animations';
      style.textContent = `
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(100px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes fadeOut {
          from {
            opacity: 1;
            transform: translateX(0);
          }
          to {
            opacity: 0;
            transform: translateX(100px);
          }
        }
      `;
      document.head.appendChild(style);
    }

    // DOM'a ekle
    document.body.appendChild(notification);

    // 3 saniye sonra kaldır
    setTimeout(() => {
      notification.style.animation = 'fadeOut 0.3s ease-out';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 3000);
  };

  // Kısayol yardım modalı
  const showShortcutHelp = () => {
    // Mevcut yardım modalını kaldır
    const existingHelp = document.querySelector('.shortcut-help-modal');
    if (existingHelp) {
      existingHelp.remove();
      return;
    }

    // Yardım modalı oluştur
    const modal = document.createElement('div');
    modal.className = 'shortcut-help-modal';
    modal.innerHTML = `
      <div class="shortcut-help-overlay">
        <div class="shortcut-help-content">
          <div class="shortcut-help-header">
            <h2>⌨️ Klavye Kısayolları</h2>
            <button class="shortcut-help-close">×</button>
          </div>
          <div class="shortcut-help-body">
            <div class="shortcut-item">
              <kbd>F1</kbd>
              <span>📊 Ana Panel (Dashboard)</span>
            </div>
            <div class="shortcut-item">
              <kbd>F2</kbd>
              <span>📋 Sipariş Yönetimi</span>
            </div>
            <div class="shortcut-item">
              <kbd>F3</kbd>
              <span>👥 Müşteri Yönetimi</span>
            </div>
            <div class="shortcut-item">
              <kbd>F4</kbd>
              <span>📦 Ürün Yönetimi</span>
            </div>
            <div class="shortcut-item">
              <kbd>F5</kbd>
              <span>🔄 Sayfayı Yenile (Tarayıcı varsayılanı)</span>
            </div>
            <div class="shortcut-item">
              <kbd>F6</kbd>
              <span>📈 Raporlar</span>
            </div>
            <div class="shortcut-item">
              <kbd>F7</kbd>
              <span>🏠 Ana Sayfa</span>
            </div>
            <div class="shortcut-item">
              <kbd>F8</kbd>
              <span>📅 Bugünkü Siparişler</span>
            </div>
            <div class="shortcut-item">
              <kbd>F9</kbd>
              <span>➕ Yeni Sipariş Ekle</span>
            </div>
            <div class="shortcut-item">
              <kbd>F10</kbd>
              <span>❓ Bu Yardım Menüsü</span>
            </div>
          </div>
          <div class="shortcut-help-footer">
            <p>💡 İpucu: Bu kısayollar tüm sayfalarda çalışır!</p>
          </div>
        </div>
      </div>
    `;

    // Stil ekle
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 10000;
    `;

    // CSS stilleri ekle
    if (!document.querySelector('#shortcut-help-styles')) {
      const style = document.createElement('style');
      style.id = 'shortcut-help-styles';
      style.textContent = `
        .shortcut-help-overlay {
          background: rgba(0, 0, 0, 0.7);
          backdrop-filter: blur(4px);
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: fadeIn 0.3s ease-out;
        }
        
        .shortcut-help-content {
          background: white;
          border-radius: 16px;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
          max-width: 500px;
          width: 90%;
          max-height: 90vh;
          overflow-y: auto;
          animation: scaleIn 0.3s ease-out;
        }
        
        .shortcut-help-header {
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
          color: white;
          padding: 20px;
          border-radius: 16px 16px 0 0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .shortcut-help-header h2 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 700;
        }
        
        .shortcut-help-close {
          background: rgba(255, 255, 255, 0.2);
          border: none;
          color: white;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          font-size: 24px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .shortcut-help-close:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: scale(1.1);
        }
        
        .shortcut-help-body {
          padding: 20px;
        }
        
        .shortcut-item {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 12px 0;
          border-bottom: 1px solid #e5e7eb;
        }
        
        .shortcut-item:last-child {
          border-bottom: none;
        }
        
        .shortcut-item kbd {
          background: linear-gradient(135deg, #f3f4f6, #e5e7eb);
          border: 1px solid #d1d5db;
          border-radius: 8px;
          padding: 8px 12px;
          font-family: monospace;
          font-weight: bold;
          font-size: 14px;
          color: #374151;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          min-width: 40px;
          text-align: center;
        }
        
        .shortcut-item span {
          font-size: 16px;
          color: #374151;
          font-weight: 500;
        }
        
        .shortcut-help-footer {
          background: #f9fafb;
          padding: 16px 20px;
          border-radius: 0 0 16px 16px;
          text-align: center;
        }
        
        .shortcut-help-footer p {
          margin: 0;
          color: #6b7280;
          font-style: italic;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes scaleIn {
          from { 
            opacity: 0;
            transform: scale(0.9) translateY(-20px);
          }
          to { 
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `;
      document.head.appendChild(style);
    }

    // Event listener'lar ekle
    const closeButton = modal.querySelector('.shortcut-help-close');
    const overlay = modal.querySelector('.shortcut-help-overlay');
    
    const closeModal = () => {
      modal.style.animation = 'fadeOut 0.3s ease-out';
      setTimeout(() => {
        if (modal.parentNode) {
          modal.parentNode.removeChild(modal);
        }
      }, 300);
    };

    closeButton?.addEventListener('click', closeModal);
    overlay?.addEventListener('click', (e) => {
      if (e.target === overlay) {
        closeModal();
      }
    });

    // ESC tuşu ile kapatma
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal();
        document.removeEventListener('keydown', handleEscKey);
      }
    };
    document.addEventListener('keydown', handleEscKey);

    // DOM'a ekle
    document.body.appendChild(modal);
  };
};

export default useKeyboardShortcuts;
